import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  smallint,
  varchar,
  text,
  mediumtext,
  longtext,
  double,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  unique,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";
import { teams } from "./hr";
import { currencies, invoices } from "./finance";
import { weeklyTimesheets } from "./core";

/**
 * Batch 1.6 — Projects / Tasks / Time logs.
 * Column names, types, defaults, nullability, FK onDelete/onUpdate and EXPLICIT
 * legacy index/unique names are VERBATIM from the legacy Laravel migrations:
 * the 2018_01_01_000000_create_worksuite_new_table.php create blocks reconciled
 * with every later up() Schema::table ALTER (down() rollbacks ignored).
 *
 * Reconciled ALTERs:
 *  - projects: +project_short_code, +mattermost_webhook_url, +mattermost_channel_id,
 *    +enable_miroboard/+miro_board_id/+client_access, +public_taskboard/+public_gantt_chart,
 *    +billing_type, +need_approval_by_admin; status enum→string(255); project_budget→double(30,2).
 *  - tasks: +task_short_code, +approval_send (enum→boolean), created_by FK re-pointed to SET NULL,
 *    start_date/due_date date→datetime.
 *  - project_milestones: +company_id (FK cascade), +add_to_budget; cost→double(30,2).
 *  - project_time_logs: +remark, +weekly_timesheet_id (FK→weekly_timesheets, deferred),
 *    +billable; earnings→double(16,2).
 *  - project_templates: +added_by(int default 1), +sub_category_id (FK→project_sub_categories).
 *  - project_template_tasks: +task_labels(text), +milestone_id (FK→project_template_milestone).
 *  - discussion_replies +added_by, pinned +added_by (2025_03_11).
 *
 * Cross-batch / cross-file FKs (currencies, invoices, teams, weekly_timesheets, etc.)
 * are now wired via lazy-arrow `.references(() => Table.col, ...)` (circular imports OK).
 * NOTE: legacy stores total_hours/total_minutes/total_break_minutes as varchar (preserved).
 * NOTE (PHP-vs-doc): project_settings/project_status_settings/task_settings/
 *   project_roadmap_settings/user_taskboard_settings are *_settings tables and are SKIPPED
 *   per batch instructions (ported with the settings batch, not here).
 */

/** project_category — create @ 2018_01_01:554. */
export const projectCategory = mysqlTable("project_category", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** project_sub_categories — create @ 2025_05_29_052859:16. category_id FK → project_category. */
export const projectSubCategories = mysqlTable(
  "project_sub_categories",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    categoryId: int("category_id", { unsigned: true })
      .notNull()
      .references(() => projectCategory.id, { onDelete: "cascade", onUpdate: "cascade" }),
    categoryName: varchar("category_name", { length: 255 }).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("client_sub_categories_category_id_foreign").on(t.categoryId)],
);

/** projects — create @ 2018_01_01:578 + ALTERs (see header). status: enum→string. */
export const projects = mysqlTable(
  "projects",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectName: varchar("project_name", { length: 255 }).notNull(),
    projectShortCode: varchar("project_short_code", { length: 255 }),
    mattermostWebhookUrl: varchar("mattermost_webhook_url", { length: 500 }),
    mattermostChannelId: varchar("mattermost_channel_id", { length: 32 }),
    projectSummary: longtext("project_summary"),
    projectAdmin: int("project_admin", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    startDate: date("start_date").notNull(),
    deadline: date("deadline"),
    notes: longtext("notes"),
    categoryId: int("category_id", { unsigned: true }).references(() => projectCategory.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    teamId: int("team_id", { unsigned: true }).references(() => teams.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }), // FK → teams
    feedback: mediumtext("feedback"),
    manualTimelog: mysqlEnum("manual_timelog", ["enable", "disable"]).default("disable"),
    clientViewTask: mysqlEnum("client_view_task", ["enable", "disable"]).default("disable"),
    allowClientNotification: mysqlEnum("allow_client_notification", ["enable", "disable"]).default(
      "disable",
    ),
    completionPercent: tinyint("completion_percent").notNull(),
    calculateTaskProgress: mysqlEnum("calculate_task_progress", ["true", "false"]).default("true"),
    billingType: varchar("billing_type", { length: 255 }),
    projectBudget: double("project_budget", { precision: 30, scale: 2 }),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → currencies
    hoursAllocated: double("hours_allocated", { precision: 8, scale: 2 }),
    status: varchar("status", { length: 255 }).default("in progress"),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    hash: text("hash"),
    public: boolean("public").notNull(),
    enableMiroboard: boolean("enable_miroboard").default(false),
    miroBoardId: varchar("miro_board_id", { length: 255 }),
    clientAccess: boolean("client_access").default(false),
    publicTaskboard: mysqlEnum("public_taskboard", ["enable", "disable"]).default("enable"),
    publicGanttChart: mysqlEnum("public_gantt_chart", ["enable", "disable"]).default("enable"),
    needApprovalByAdmin: mysqlEnum("need_approval_by_admin", ["0", "1"]).default("0"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
    deletedAt: timestamp("deleted_at"),
  },
  (t) => [
    index("projects_project_admin_foreign").on(t.projectAdmin),
    index("projects_category_id_foreign").on(t.categoryId),
    index("projects_client_id_foreign").on(t.clientId),
    index("projects_team_id_foreign").on(t.teamId),
    index("projects_currency_id_foreign").on(t.currencyId),
    index("projects_added_by_foreign").on(t.addedBy),
    index("projects_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_milestones — create @ 2018_01_01:2005 + company_id ALTER + add_to_budget. cost→double(30,2). */
export const projectMilestones = mysqlTable(
  "project_milestones",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → currencies
    milestoneTitle: varchar("milestone_title", { length: 255 }).notNull(),
    summary: mediumtext("summary").notNull(),
    cost: double("cost", { precision: 30, scale: 2 }).notNull(),
    status: mysqlEnum("status", ["complete", "incomplete"]).default("incomplete"),
    addToBudget: mysqlEnum("add_to_budget", ["yes", "no"]).default("no"),
    invoiceCreated: boolean("invoice_created").notNull(),
    invoiceId: int("invoice_id"),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    startDate: date("start_date"),
    endDate: date("end_date"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_milestones_company_id_index").on(t.companyId),
    index("project_milestones_project_id_foreign").on(t.projectId),
    index("project_milestones_currency_id_foreign").on(t.currencyId),
    index("project_milestones_added_by_foreign").on(t.addedBy),
    index("project_milestones_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** task_category — create @ 2018_01_01:2026. */
export const taskCategory = mysqlTable(
  "task_category",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    categoryName: varchar("category_name", { length: 255 }).notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_category_added_by_foreign").on(t.addedBy),
    index("task_category_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** tasks — create @ 2018_01_01:2038 + ALTERs (task_short_code, approval_send bool, start/due→datetime). */
export const tasks = mysqlTable(
  "tasks",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    taskShortCode: varchar("task_short_code", { length: 255 }),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    heading: varchar("heading", { length: 255 }).notNull(),
    description: longtext("description"),
    dueDate: datetime("due_date"),
    startDate: datetime("start_date"),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    taskCategoryId: int("task_category_id", { unsigned: true }).references(() => taskCategory.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    priority: mysqlEnum("priority", ["low", "medium", "high"]).default("medium"),
    status: mysqlEnum("status", ["incomplete", "completed"]).default("incomplete"),
    boardColumnId: int("board_column_id", { unsigned: true })
      .default(1)
      .references(() => taskboardColumns.id, { onDelete: "set null", onUpdate: "cascade" }),
    columnPriority: int("column_priority").notNull(),
    completedOn: datetime("completed_on"),
    createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    recurringTaskId: int("recurring_task_id", { unsigned: true }).references(
      (): AnyMySqlColumn => tasks.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    dependentTaskId: int("dependent_task_id", { unsigned: true }).references(
      (): AnyMySqlColumn => tasks.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    milestoneId: int("milestone_id", { unsigned: true }).references(() => projectMilestones.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    isPrivate: boolean("is_private").default(false),
    billable: boolean("billable").default(true),
    estimateHours: int("estimate_hours").notNull(),
    estimateMinutes: int("estimate_minutes").notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    hash: varchar("hash", { length: 64 }),
    repeat: boolean("repeat").default(false),
    repeatComplete: boolean("repeat_complete").default(false),
    repeatCount: int("repeat_count"),
    repeatType: mysqlEnum("repeat_type", ["day", "week", "month", "year"]).default("day"),
    repeatCycles: int("repeat_cycles"),
    eventId: text("event_id"),
    approvalSend: boolean("approval_send").default(false),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
    deletedAt: timestamp("deleted_at"),
  },
  (t) => [
    index("tasks_project_id_foreign").on(t.projectId),
    index("tasks_task_category_id_foreign").on(t.taskCategoryId),
    index("tasks_board_column_id_foreign").on(t.boardColumnId),
    index("tasks_created_by_foreign").on(t.createdBy),
    index("tasks_recurring_task_id_foreign").on(t.recurringTaskId),
    index("tasks_dependent_task_id_foreign").on(t.dependentTaskId),
    index("tasks_milestone_id_foreign").on(t.milestoneId),
    index("tasks_added_by_foreign").on(t.addedBy),
    index("tasks_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** taskboard_columns — create @ 2018_01_01:41 (+ company_id ALTER). column_name unique. */
export const taskboardColumns = mysqlTable("taskboard_columns", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }),
  columnName: varchar("column_name", { length: 255 }).notNull().unique(),
  slug: varchar("slug", { length: 255 }),
  labelColor: varchar("label_color", { length: 255 }).notNull(),
  priority: int("priority").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** pinned — create @ 2018_01_01:2083 (+ added_by ALTER 2025_03_11). */
export const pinned = mysqlTable(
  "pinned",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    taskId: int("task_id", { unsigned: true }).references(() => tasks.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("pinned_project_id_foreign").on(t.projectId),
    index("pinned_task_id_foreign").on(t.taskId),
    index("pinned_user_id_foreign").on(t.userId),
    index("pinned_added_by_foreign").on(t.addedBy),
  ],
);

/** project_activity — create @ 2018_01_01:2109. */
export const projectActivity = mysqlTable(
  "project_activity",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    activity: text("activity").notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("project_activity_project_id_foreign").on(t.projectId)],
);

/** project_files — create @ 2018_01_01:2118. */
export const projectFiles = mysqlTable(
  "project_files",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    filename: varchar("filename", { length: 255 }).notNull(),
    hashname: varchar("hashname", { length: 255 }),
    size: varchar("size", { length: 255 }),
    description: text("description"),
    googleUrl: varchar("google_url", { length: 255 }),
    dropboxLink: varchar("dropbox_link", { length: 255 }),
    externalLinkName: varchar("external_link_name", { length: 255 }),
    externalLink: text("external_link"),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_files_user_id_foreign").on(t.userId),
    index("project_files_project_id_foreign").on(t.projectId),
    index("project_files_added_by_foreign").on(t.addedBy),
    index("project_files_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_members — create @ 2018_01_01:2141. */
export const projectMembers = mysqlTable(
  "project_members",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    hourlyRate: double("hourly_rate").notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_members_user_id_foreign").on(t.userId),
    index("project_members_project_id_foreign").on(t.projectId),
    index("project_members_added_by_foreign").on(t.addedBy),
    index("project_members_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_notes — create @ 2018_01_01:2155. */
export const projectNotes = mysqlTable(
  "project_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    title: varchar("title", { length: 255 }).notNull(),
    type: boolean("type").default(false),
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    isClientShow: boolean("is_client_show").default(false),
    askPassword: boolean("ask_password").default(false),
    details: longtext("details").notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_notes_project_id_foreign").on(t.projectId),
    index("project_notes_client_id_foreign").on(t.clientId),
    index("project_notes_added_by_foreign").on(t.addedBy),
    index("project_notes_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_user_notes — create @ 2018_01_01:2308. */
export const projectUserNotes = mysqlTable(
  "project_user_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    projectNoteId: int("project_note_id", { unsigned: true })
      .notNull()
      .references(() => projectNotes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_user_notes_user_id_foreign").on(t.userId),
    index("project_user_notes_project_note_id_foreign").on(t.projectNoteId),
  ],
);

/** project_ratings — create @ 2018_01_01:2173. */
export const projectRatings = mysqlTable(
  "project_ratings",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    rating: double("rating").default(0),
    comment: text("comment"),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_ratings_project_id_foreign").on(t.projectId),
    index("project_ratings_user_id_foreign").on(t.userId),
    index("project_ratings_added_by_foreign").on(t.addedBy),
    index("project_ratings_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_departments — create @ 2024_06_11_063115:16. project_id is signed-via-unsigned int. */
export const projectDepartments = mysqlTable(
  "project_departments",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    teamId: int("team_id", { unsigned: true })
      .notNull()
      .references(() => teams.id, { onDelete: "cascade", onUpdate: "cascade" }), // FK → teams
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("project_departments_team_id_index").on(t.teamId)],
);

/** project_label_list — create @ 2025_07_14_062501:14. */
export const projectLabelList = mysqlTable(
  "project_label_list",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    labelName: varchar("label_name", { length: 255 }).notNull(),
    color: varchar("color", { length: 255 }),
    description: varchar("description", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("project_labels_added_by_foreign").on(t.addedBy)],
);

/** project_labels — create @ 2025_07_14_062501:26. (pivot; explicit FK name on project_id) */
export const projectLabels = mysqlTable(
  "project_labels",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    labelId: int("label_id", { unsigned: true })
      .notNull()
      .references(() => projectLabelList.id, { onDelete: "cascade", onUpdate: "cascade" }),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_labels_label_id_foreign").on(t.labelId),
    index("project_tags_project_id_foreign").on(t.projectId),
  ],
);

/** project_qa_summaries — create @ 2026_05_21_140000:16. project_id unique. */
export const projectQaSummaries = mysqlTable(
  "project_qa_summaries",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    projectId: int("project_id", { unsigned: true })
      .notNull()
      .references(() => projects.id, { onDelete: "cascade" }),
    overview: text("overview"),
    engagementStatus: mysqlEnum("engagement_status", [
      "onboarding",
      "active",
      "paused",
      "wrapped",
    ]).default("onboarding"),
    platforms: text("platforms"), // json
    qaScope: text("qa_scope"), // json
    testingApproach: text("testing_approach"),
    qaLeadId: int("qa_lead_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
    }),
    qaTeamSize: smallint("qa_team_size", { unsigned: true }),
    devTeamSize: smallint("dev_team_size", { unsigned: true }),
    bugTrackingTool: varchar("bug_tracking_tool", { length: 255 }),
    testManagementTool: varchar("test_management_tool", { length: 255 }),
    automationTool: varchar("automation_tool", { length: 255 }),
    automationLanguage: varchar("automation_language", { length: 255 }),
    communicationTool: varchar("communication_tool", { length: 255 }),
    buildSource: varchar("build_source", { length: 255 }),
    stagingUrl: varchar("staging_url", { length: 500 }),
    productionUrl: varchar("production_url", { length: 500 }),
    devicesBrowsers: text("devices_browsers"),
    deliverables: text("deliverables"),
    clientChallenges: text("client_challenges"),
    qaSolutions: text("qa_solutions"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    unique().on(t.projectId),
    index("project_qa_summaries_company_status_index").on(t.companyId, t.engagementStatus),
  ],
);

/** project_user_remarks — create @ 2026_06_01_120000:20. No FK constraints in legacy (plain bigints). */
export const projectUserRemarks = mysqlTable(
  "project_user_remarks",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: bigint("company_id", { mode: "number", unsigned: true }), // FK → companies (no legacy constraint)
    userId: bigint("user_id", { mode: "number", unsigned: true }).notNull(), // FK → users (no legacy constraint)
    projectId: bigint("project_id", { mode: "number", unsigned: true }), // FK → projects (no legacy constraint)
    timelogId: bigint("timelog_id", { mode: "number", unsigned: true }), // FK → project_time_logs (no legacy constraint)
    type: mysqlEnum("type", ["praise", "concern", "note"]).default("note"),
    category: varchar("category", { length: 64 }),
    body: text("body").notNull(),
    addedBy: bigint("added_by", { mode: "number", unsigned: true }).notNull(), // FK → users (no legacy constraint)
    visibleToEmployee: boolean("visible_to_employee").default(false),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_user_remarks_company_id_index").on(t.companyId),
    index("pur_co_user_at_idx").on(t.companyId, t.userId, t.createdAt),
    index("pur_co_proj_idx").on(t.companyId, t.projectId),
    index("pur_co_author_idx").on(t.companyId, t.addedBy),
  ],
);

/** project_templates — create @ 2018_01_01:2199 + added_by(int default 1) + sub_category_id. */
export const projectTemplates = mysqlTable(
  "project_templates",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectName: varchar("project_name", { length: 255 }).notNull(),
    categoryId: int("category_id", { unsigned: true }).references(() => projectCategory.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    subCategoryId: bigint("sub_category_id", { mode: "number", unsigned: true }).references(
      () => projectSubCategories.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    projectSummary: mediumtext("project_summary"),
    notes: longtext("notes"),
    feedback: mediumtext("feedback"),
    clientViewTask: mysqlEnum("client_view_task", ["enable", "disable"]).default("disable"),
    allowClientNotification: mysqlEnum("allow_client_notification", ["enable", "disable"]).default(
      "disable",
    ),
    manualTimelog: mysqlEnum("manual_timelog", ["enable", "disable"]).default("disable"),
    addedBy: int("added_by").default(1).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_templates_category_id_foreign").on(t.categoryId),
    index("project_templates_client_id_foreign").on(t.clientId),
  ],
);

/** project_template_members — create @ 2018_01_01:2217. */
export const projectTemplateMembers = mysqlTable(
  "project_template_members",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    projectTemplateId: int("project_template_id", { unsigned: true })
      .notNull()
      .references(() => projectTemplates.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_template_members_user_id_foreign").on(t.userId),
    index("project_template_members_project_template_id_foreign").on(t.projectTemplateId),
  ],
);

/** project_template_milestone — create @ 2025_05_28_072917:15. cost double(30,2). */
export const projectTemplateMilestone = mysqlTable(
  "project_template_milestone",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectTemplateId: int("project_template_id", { unsigned: true }).references(
      () => projectTemplates.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → currencies
    milestoneTitle: varchar("milestone_title", { length: 255 }).notNull(),
    summary: mediumtext("summary").notNull(),
    cost: double("cost", { precision: 30, scale: 2 }).notNull(),
    status: mysqlEnum("status", ["complete", "incomplete"]).default("incomplete"),
    addToBudget: mysqlEnum("add_to_budget", ["yes", "no"]).default("no"),
    invoiceCreated: boolean("invoice_created").notNull(),
    invoiceId: int("invoice_id"),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    startDate: date("start_date"),
    endDate: date("end_date"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_milestones_company_id_index").on(t.companyId),
    index("project_milestones_project_id_foreign").on(t.projectTemplateId),
    index("project_milestones_currency_id_foreign").on(t.currencyId),
    index("project_milestones_added_by_foreign").on(t.addedBy),
    index("project_milestones_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_template_tasks — create @ 2018_01_01:2226 + task_labels(text) + milestone_id. */
export const projectTemplateTasks = mysqlTable(
  "project_template_tasks",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    heading: varchar("heading", { length: 255 }).notNull(),
    description: mediumtext("description"),
    projectTemplateId: int("project_template_id", { unsigned: true })
      .notNull()
      .references(() => projectTemplates.id, { onDelete: "cascade", onUpdate: "cascade" }),
    milestoneId: int("milestone_id", { unsigned: true }).references(
      () => projectTemplateMilestone.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    priority: mysqlEnum("priority", ["low", "medium", "high"]).default("medium"),
    taskLabels: text("task_labels"),
    projectTemplateTaskCategoryId: int("project_template_task_category_id", {
      unsigned: true,
    }).references(() => taskCategory.id, { onDelete: "set null", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_template_tasks_project_template_id_foreign").on(t.projectTemplateId),
    index("tasks_milestone_id_foreign").on(t.milestoneId),
    index("project_template_tasks_project_template_task_category_id_foreign").on(
      t.projectTemplateTaskCategoryId,
    ),
  ],
);

/** project_template_sub_tasks — create @ 2018_01_01:2238. */
export const projectTemplateSubTasks = mysqlTable(
  "project_template_sub_tasks",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    projectTemplateTaskId: int("project_template_task_id", { unsigned: true })
      .notNull()
      .references(() => projectTemplateTasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    title: text("title").notNull(),
    startDate: datetime("start_date"),
    dueDate: datetime("due_date"),
    status: mysqlEnum("status", ["incomplete", "complete"]).default("incomplete"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_template_sub_tasks_project_template_task_id_foreign").on(t.projectTemplateTaskId),
  ],
);

/** project_template_task_users — create @ 2018_01_01:2249. */
export const projectTemplateTaskUsers = mysqlTable(
  "project_template_task_users",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    projectTemplateTaskId: int("project_template_task_id", { unsigned: true })
      .notNull()
      .references(() => projectTemplateTasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_template_task_users_project_template_task_id_foreign").on(
      t.projectTemplateTaskId,
    ),
    index("project_template_task_users_user_id_foreign").on(t.userId),
  ],
);

/** project_time_logs — create @ 2018_01_01:2258 + remark + weekly_timesheet_id + billable. earnings→double(16,2). */
export const projectTimeLogs = mysqlTable(
  "project_time_logs",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    taskId: int("task_id", { unsigned: true }).references(() => tasks.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    startTime: datetime("start_time").notNull(),
    endTime: datetime("end_time"),
    memo: text("memo").notNull(),
    remark: text("remark"),
    totalHours: varchar("total_hours", { length: 255 }),
    totalMinutes: varchar("total_minutes", { length: 255 }),
    editedByUser: int("edited_by_user", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    hourlyRate: int("hourly_rate").notNull(),
    earnings: double("earnings", { precision: 16, scale: 2 }).notNull(),
    billable: boolean("billable").default(true),
    approved: boolean("approved").default(true),
    approvedBy: int("approved_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    invoiceId: int("invoice_id", { unsigned: true }).references(() => invoices.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }), // FK → invoices
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    totalBreakMinutes: varchar("total_break_minutes", { length: 255 }),
    weeklyTimesheetId: bigint("weekly_timesheet_id", { mode: "number", unsigned: true }).references(
      () => weeklyTimesheets.id,
      { onDelete: "cascade" },
    ), // FK → weekly_timesheets
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("project_time_logs_project_id_foreign").on(t.projectId),
    index("project_time_logs_task_id_foreign").on(t.taskId),
    index("project_time_logs_user_id_foreign").on(t.userId),
    index("project_time_logs_edited_by_user_foreign").on(t.editedByUser),
    index("project_time_logs_approved_by_foreign").on(t.approvedBy),
    index("project_time_logs_invoice_id_foreign").on(t.invoiceId),
    index("project_time_logs_added_by_foreign").on(t.addedBy),
    index("project_time_logs_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** project_time_log_breaks — create @ 2018_01_01:2290. */
export const projectTimeLogBreaks = mysqlTable(
  "project_time_log_breaks",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectTimeLogId: int("project_time_log_id", { unsigned: true }).references(
      () => projectTimeLogs.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    startTime: datetime("start_time").notNull(),
    endTime: datetime("end_time"),
    reason: text("reason").notNull(),
    totalHours: varchar("total_hours", { length: 255 }),
    totalMinutes: varchar("total_minutes", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("project_time_log_breaks_project_time_log_id_foreign").on(t.projectTimeLogId)],
);

/** project_time_log_tasks — create @ 2025_08_11_122122:14. No FK constraints in legacy (plain unsigned ints). */
export const projectTimeLogTasks = mysqlTable("project_time_log_tasks", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  projectTimeLogId: int("project_time_log_id", { unsigned: true }), // FK → project_time_logs (no legacy constraint)
  taskId: int("task_id", { unsigned: true }), // FK → tasks (no legacy constraint)
  taskStatus: varchar("task_status", { length: 255 }),
  hours: varchar("hours", { length: 255 }),
  taskNote: text("task_note"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** sub_tasks — create @ 2018_01_01:2578. */
export const subTasks = mysqlTable(
  "sub_tasks",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    title: text("title").notNull(),
    dueDate: datetime("due_date"),
    startDate: date("start_date"),
    status: mysqlEnum("status", ["incomplete", "complete"]).default("incomplete"),
    assignedTo: int("assigned_to", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    description: text("description"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("sub_tasks_task_id_foreign").on(t.taskId),
    index("sub_tasks_assigned_to_foreign").on(t.assignedTo),
    index("sub_tasks_added_by_foreign").on(t.addedBy),
    index("sub_tasks_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** sub_task_files — create @ 2018_01_01:2596. */
export const subTaskFiles = mysqlTable(
  "sub_task_files",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    subTaskId: int("sub_task_id", { unsigned: true })
      .notNull()
      .references(() => subTasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    filename: varchar("filename", { length: 255 }).notNull(),
    description: text("description"),
    googleUrl: varchar("google_url", { length: 255 }),
    hashname: varchar("hashname", { length: 255 }),
    size: varchar("size", { length: 255 }),
    dropboxLink: varchar("dropbox_link", { length: 255 }),
    externalLink: varchar("external_link", { length: 255 }),
    externalLinkName: varchar("external_link_name", { length: 255 }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("sub_task_files_user_id_foreign").on(t.userId),
    index("sub_task_files_sub_task_id_foreign").on(t.subTaskId),
  ],
);

/** task_comments — create @ 2018_01_01:2613. */
export const taskComments = mysqlTable(
  "task_comments",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    comment: text("comment").notNull(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_comments_user_id_foreign").on(t.userId),
    index("task_comments_task_id_foreign").on(t.taskId),
    index("task_comments_added_by_foreign").on(t.addedBy),
    index("task_comments_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** task_comment_emoji — create @ 2022_12_05_062331:20. */
export const taskCommentEmoji = mysqlTable("task_comment_emoji", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  commentId: int("comment_id", { unsigned: true }).references(() => taskComments.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  emojiName: varchar("emoji_name", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** task_files — create @ 2018_01_01:2627. */
export const taskFiles = mysqlTable(
  "task_files",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    filename: varchar("filename", { length: 255 }).notNull(),
    description: text("description"),
    googleUrl: varchar("google_url", { length: 255 }),
    hashname: varchar("hashname", { length: 255 }),
    size: varchar("size", { length: 255 }),
    dropboxLink: varchar("dropbox_link", { length: 255 }),
    externalLink: varchar("external_link", { length: 255 }),
    externalLinkName: varchar("external_link_name", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_files_user_id_foreign").on(t.userId),
    index("task_files_task_id_foreign").on(t.taskId),
    index("task_files_added_by_foreign").on(t.addedBy),
    index("task_files_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** task_history — create @ 2018_01_01:2648. */
export const taskHistory = mysqlTable(
  "task_history",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    subTaskId: int("sub_task_id", { unsigned: true }).references(() => subTasks.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    details: text("details").notNull(),
    boardColumnId: int("board_column_id", { unsigned: true }).references(
      () => taskboardColumns.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_history_task_id_foreign").on(t.taskId),
    index("task_history_sub_task_id_foreign").on(t.subTaskId),
    index("task_history_user_id_foreign").on(t.userId),
    index("task_history_board_column_id_foreign").on(t.boardColumnId),
  ],
);

/** task_label_list — create @ 2018_01_01:2662 (project_id added in same up()-flow ALTER). */
export const taskLabelList = mysqlTable(
  "task_label_list",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    labelName: varchar("label_name", { length: 255 }).notNull(),
    color: varchar("color", { length: 255 }),
    description: varchar("description", { length: 255 }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("task_label_list_project_id_foreign").on(t.projectId)],
);

/** task_labels — create @ 2018_01_01:2674 (pivot; explicit FK name on task_id). */
export const taskLabels = mysqlTable(
  "task_labels",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    labelId: int("label_id", { unsigned: true })
      .notNull()
      .references(() => taskLabelList.id, { onDelete: "cascade", onUpdate: "cascade" }),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_labels_label_id_foreign").on(t.labelId),
    index("task_tags_task_id_foreign").on(t.taskId),
  ],
);

/** task_notes — create @ 2018_01_01:2683. user_id is plain signed int (no FK). */
export const taskNotes = mysqlTable(
  "task_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id"),
    note: text("note"),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_notes_task_id_foreign").on(t.taskId),
    index("task_notes_added_by_foreign").on(t.addedBy),
    index("task_notes_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** task_users — create @ 2018_01_01:2696. */
export const taskUsers = mysqlTable(
  "task_users",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    taskId: int("task_id", { unsigned: true })
      .notNull()
      .references(() => tasks.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("task_users_task_id_foreign").on(t.taskId),
    index("task_users_user_id_foreign").on(t.userId),
  ],
);

/** discussion_categories — create @ 2018_01_01:757. */
export const discussionCategories = mysqlTable("discussion_categories", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  order: int("order").default(1),
  name: varchar("name", { length: 255 }).notNull(),
  color: varchar("color", { length: 20 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** discussion_replies — create @ 2018_01_01:767 (FKs + added_by added in ALTERs). */
export const discussionReplies = mysqlTable(
  "discussion_replies",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    discussionId: int("discussion_id", { unsigned: true })
      .notNull()
      .references((): AnyMySqlColumn => discussions.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
      }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    body: longtext("body").notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    deletedAt: timestamp("deleted_at"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("discussion_replies_discussion_id_foreign").on(t.discussionId),
    index("discussion_replies_user_id_foreign").on(t.userId),
    index("discussion_replies_added_by_foreign").on(t.addedBy),
  ],
);

/** discussions — create @ 2018_01_01:777 (FKs added in ALTER). last_reply_at useCurrent. */
export const discussions = mysqlTable(
  "discussions",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    discussionCategoryId: int("discussion_category_id", { unsigned: true })
      .default(1)
      .references(() => discussionCategories.id, { onDelete: "set null", onUpdate: "cascade" }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    title: varchar("title", { length: 255 }).notNull(),
    color: varchar("color", { length: 20 }).default("#232629"),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    pinned: boolean("pinned").default(false),
    closed: boolean("closed").default(false),
    deletedAt: timestamp("deleted_at"),
    lastReplyAt: timestamp("last_reply_at").defaultNow(),
    bestAnswerId: int("best_answer_id", { unsigned: true }).references(() => discussionReplies.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastReplyById: int("last_reply_by_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("discussions_discussion_category_id_foreign").on(t.discussionCategoryId),
    index("discussions_project_id_foreign").on(t.projectId),
    index("discussions_user_id_foreign").on(t.userId),
    index("discussions_best_answer_id_foreign").on(t.bestAnswerId),
    index("discussions_last_reply_by_id_foreign").on(t.lastReplyById),
    index("discussions_added_by_foreign").on(t.addedBy),
    index("discussions_last_updated_by_foreign").on(t.lastUpdatedBy),
  ],
);

/** discussion_files — create @ 2018_01_01:813. */
export const discussionFiles = mysqlTable(
  "discussion_files",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    discussionId: int("discussion_id", { unsigned: true }).references(() => discussions.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    discussionReplyId: int("discussion_reply_id", { unsigned: true }).references(
      () => discussionReplies.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    filename: varchar("filename", { length: 255 }).notNull(),
    description: text("description"),
    googleUrl: varchar("google_url", { length: 255 }),
    hashname: varchar("hashname", { length: 255 }),
    size: varchar("size", { length: 255 }),
    dropboxLink: varchar("dropbox_link", { length: 255 }),
    externalLinkName: varchar("external_link_name", { length: 255 }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("discussion_files_user_id_foreign").on(t.userId),
    index("discussion_files_discussion_id_foreign").on(t.discussionId),
    index("discussion_files_discussion_reply_id_foreign").on(t.discussionReplyId),
  ],
);
